import { useState, useMemo, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useContacts } from '../hooks/useContacts';
import { Edit, Mail, Building2, Eye, EyeOff, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';
import { useContact } from '../hooks/useContact';

import EditContactPopup from '../components/contacts/EditContactPopup';
import UnifiedTimeline from '../components/timeline/UnifiedTimeline';
import { useUserSettings } from '../hooks/useUserSettings';
import { useAnonymization } from '../utils/anonymize';
import { useGmailMessages } from '../hooks/useGmailMessages';
import { useTimelineEntries } from '../hooks/useTimelineEntries';
import TimelineEntryPopup from '../components/timeline/TimelineEntryPopup';
import FollowUpReminderPopup from '../components/timeline/FollowUpReminderPopup';
import FollowUpReminders from '../components/timeline/FollowUpReminders';
import LoadingPage from '../components/ui/LoadingPage';
import GmailComposePopup from '../components/email/GmailComposePopup';
import ContactSearchPopover from '../components/contacts/ContactSearchPopover';

function getGmailComposeUrl(email: string) {
  return `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(email)}`;
}

export default function ContactDetails() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { contact, loading, error, refetch } = useContact(id!);
  const { settings } = useUserSettings();
  const [temporaryReveal, setTemporaryReveal] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [showAddFollowUp, setShowAddFollowUp] = useState(false);
  const [showEmailCompose, setShowEmailCompose] = useState(false);
  const [showSearch, setShowSearch] = useState(false);

  const shouldAnonymize = (settings?.anonymizing_mode ?? false) && !temporaryReveal;

  const { contacts } = useContacts();

  // Track if any popup is open
  const isPopupOpen = showAddFollowUp || showAddEntry || showEdit;

  // Handle keyboard shortcuts
  useEffect(() => {
    // Don't attach handlers if any popup is open
    if (isPopupOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        navigate('/contacts');
        return;
      }
      
      // Check if CMD/Ctrl is pressed
      if (e.metaKey || e.ctrlKey) {
        switch (e.key.toLowerCase()) {
          case 'm':
            e.preventDefault();
            if (contact?.work_email || contact?.personal_email) {
              setShowEmailCompose(true);
            }
            break;
          case 'f':
            e.preventDefault(); // Prevent browser's find function
            setShowAddFollowUp(true);
            break;
          case 'e':
            e.preventDefault();
            setShowEdit(true);
            break;
          case 'a':
            e.preventDefault(); // Prevent select all
            setShowAddEntry(true);
            break;
          case ']':
            e.preventDefault();
            if (contacts.length > 0 && id) {
              const currentIndex = contacts.findIndex(c => c.id === id);
              if (currentIndex !== -1 && currentIndex < contacts.length - 1) {
                const nextContact = contacts[currentIndex + 1];
                navigate(`/contacts/${nextContact.id}`);
              }
            }
            break;
          case '[':
            e.preventDefault();
            if (contacts.length > 0 && id) {
              const currentIndex = contacts.findIndex(c => c.id === id);
              if (currentIndex > 0) {
                const prevContact = contacts[currentIndex - 1];
                navigate(`/contacts/${prevContact.id}`);
              }
            }
            break;
          case 'o':
            e.preventDefault();
            setShowSearch(true);
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [contacts, id, isPopupOpen, navigate, contact]);

  // Collect all email addresses
  const contactEmails = useMemo(() => {
    if (!contact) return [];
    return [contact.work_email, contact.personal_email].filter(Boolean) as string[];
  }, [contact]);

  const { messages, refreshing, refetch: refetchEmails } = useGmailMessages(contactEmails);
  const { entries, refetch: refetchTimeline } = useTimelineEntries(id!);

  if (loading) {
    return (
      <LoadingPage message="Loading contact details..." />
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-red-500">{error}</div>
      </div>
    );
  }

  if (!contact) {
    return (
      <LoadingPage message="Contact not found" />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-bold">Contact Details</h1>
          {settings?.anonymizing_mode && (
            <button
              onClick={() => setTemporaryReveal(!temporaryReveal)}
              className="btn btn-secondary flex items-center gap-2 ml-4"
              title={temporaryReveal ? "Hide sensitive data" : "Reveal sensitive data"}
            >
              {temporaryReveal ? (
                <>
                  <EyeOff className="w-4 h-4" />
                  Hide
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4" />
                  Reveal
                </>
              )}
            </button>
          )}
        </div>
        <div className="flex items-center gap-4">
          {contact.company && (
            <Link
              to={`/companies/${contact.company.id}`}
              className="btn btn-secondary flex items-center gap-2"
            >
              <Building2 className="w-4 h-4" />
              {useAnonymization(contact.company.name, 'company', shouldAnonymize ?? false)}
            </Link>
          )}
          <button
            onClick={() => setShowEdit(true)}
            className="btn btn-secondary flex items-center gap-2"
          >
            <Edit className="w-4 h-4" />
            Edit
            <span className="text-xs text-gray-400">⌘E</span>
          </button>
          {(contact.work_email || contact.personal_email) && (
            <button 
              onClick={() => setShowEmailCompose(true)}
              className="btn btn-primary flex items-center gap-2"
            >
              <Mail className="w-4 h-4" />
              Send Email
              <span className="text-xs text-gray-400">⌘M</span>
            </button>
          )}
        </div>
      </div>

      <div className="space-y-6">
        {/* Contact Information Card */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold mb-4">Contact Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-400">Name</p>
                <p className="text-lg">
                  {useAnonymization(contact.first_name, 'name', shouldAnonymize ?? false)}{' '}
                  {useAnonymization(contact.last_name, 'name', shouldAnonymize ?? false)}
                </p>
              </div>

              {(contact.company || contact.job_title) && (
                <div>
                  <p className="text-sm text-gray-400">Company</p>
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-gray-400" />
                    <div>
                      {contact.company ? (
                        <Link 
                          to={`/companies/${contact.company.id}`}
                          state={{ from: location.pathname }}
                          className="text-primary-500 hover:text-primary-400"
                        >
                          {useAnonymization(contact.company.name, 'company', shouldAnonymize ?? false)}
                        </Link>
                      ) : (
                        <span className="text-gray-400">No company</span>
                      )}
                      {contact.job_title && (
                        <p className="text-sm text-gray-400 mt-1">
                          {contact.job_title}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {contact.birthday && (
                <div>
                  <p className="text-sm text-gray-400">Birthday</p>
                  <p className="text-lg">{format(new Date(`${contact.birthday}T12:00:00Z`), 'MMMM d')}</p>
                </div>
              )}
            </div>

            <div className="space-y-4">
              {contact.work_email && (
                <div>
                  <p className="text-sm text-gray-400">Work Email</p>
                  <p className="text-lg">
                    {useAnonymization(contact.work_email, 'email', shouldAnonymize ?? false)}
                  </p>
                </div>
              )}
              {contact.personal_email && (
                <div>
                  <p className="text-sm text-gray-400">Personal Email</p>
                  <p className="text-lg">
                    {useAnonymization(contact.personal_email, 'email', shouldAnonymize ?? false)}
                  </p>
                </div>
              )}
              {contact.phone_number && (
                <div>
                  <p className="text-sm text-gray-400">Phone Number</p>
                  <p className="text-lg">{contact.phone_number}</p>
                </div>
              )}
              {contact.work_phone && (
                <div>
                  <p className="text-sm text-gray-400">Work Phone</p>
                  <p className="text-lg">{contact.work_phone}</p>
                </div>
              )}
              {contact.linkedin_url && (
                <div>
                  <p className="text-sm text-gray-400">LinkedIn</p>
                  <a 
                    href={contact.linkedin_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-500 hover:text-primary-400"
                  >
                    View Profile
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Additional Details Card */}
        {(contact.significant_other || contact.children_details || contact.notes) && (
          <div className="card p-6">
            <h2 className="text-lg font-semibold mb-4">Additional Details</h2>
            <div className="space-y-4">
              {contact.significant_other && (
                <div>
                  <p className="text-sm text-gray-400">Significant Other</p>
                  <p className="text-lg">{contact.significant_other}</p>
                </div>
              )}
              {contact.children_details && (
                <div>
                  <p className="text-sm text-gray-400">Children</p>
                  <p className="text-lg">{contact.children_details}</p>
                </div>
              )}
              {contact.notes && (
                <div>
                  <p className="text-sm text-gray-400">Notes</p>
                  <p className="text-lg whitespace-pre-wrap">{contact.notes}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Follow Up Reminders */}
        <FollowUpReminders contactId={contact.id} />

        {/* Timeline Section */}
        <div className="card p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold">Timeline</h2>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowAddFollowUp(true)}
                className="btn btn-secondary flex items-center gap-2"
              >
                Add Follow Up
                <span className="text-xs text-gray-400">⌘F</span>
              </button>
              <button
                onClick={() => setShowAddEntry(true)}
                className="btn btn-secondary flex items-center gap-2"
              >
                Add Entry
                <span className="text-xs text-gray-400">⌘A</span>
              </button>
              <button
                onClick={() => refetchEmails()}
                disabled={refreshing}
                className="btn btn-secondary flex items-center gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                {refreshing ? 'Refreshing...' : 'Refresh Emails'}
              </button>
            </div>
          </div>

          <UnifiedTimeline
            contactId={contact.id}
            timelineEntries={entries}
            emailMessages={messages}
            onUpdate={() => {
              refetchTimeline();
              refetch();
            }}
            onRefreshEmails={refetchEmails}
            refreshing={refreshing}
          />
        </div>
      </div>

      {showEdit && (
        <EditContactPopup
          contact={contact}
          onClose={() => setShowEdit(false)}
          onSuccess={() => {
            refetch();
            setShowEdit(false);
          }}
        />
      )}

      {showAddEntry && (
        <TimelineEntryPopup
          contactId={contact.id}
          onClose={() => setShowAddEntry(false)}
          onSuccess={() => {
            refetchTimeline();
            setShowAddEntry(false);
          }}
        />
      )}

      {showAddFollowUp && (
        <FollowUpReminderPopup
          contactId={contact.id}
          onClose={() => setShowAddFollowUp(false)}
          onSuccess={() => {
            refetchTimeline();
            setShowAddFollowUp(false);
          }}
        />
      )}

      {showEmailCompose && contact && (
        <GmailComposePopup
          email={contact.work_email || contact.personal_email}
          subject="Following up"
          template={`Hi ${contact.first_name},

I hope this email finds you well.

[Your message here]

Best regards,
${settings?.user_name || '[Your name]'}
${settings?.job_title || '[Your title]'}
`}
          onClose={() => setShowEmailCompose(false)}
        />
      )}

      {showSearch && (
        <ContactSearchPopover
          onClose={() => setShowSearch(false)}
        />
      )}
    </div>
  );
}